import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";
import {
  DeleteCompoundRecordComposerDocument,
  type DeleteCompoundRecordComposerMutation,
  type DeleteCompoundRecordComposerMutationVariables,
} from "~/features/compoundRecordComposers/gqls";
import { useTenant } from "~/features/tenant/context";

export function useComposerDelete({ onSuccess }: { onSuccess?: () => void } = {}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const [processing, setProcessing] = useState(false);
  const [mutate] = useMutation<DeleteCompoundRecordComposerMutation, DeleteCompoundRecordComposerMutationVariables>(
    DeleteCompoundRecordComposerDocument,
  );

  const onDelete = useCallback(
    async (id: string) => {
      setProcessing(true);
      try {
        const res = await mutate({ variables: { tenantId, id } });
        match(res.data?.cnutDeleteCompoundRecordComposer)
          .with({ __typename: "CompoundRecordComposerDeleteOutput" }, () => {
            toast.success(t("feature.common.deletedSuccessfully", { model: "compoundRecordComposer" }));
            onSuccess?.();
          })
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.message))
          .otherwise(() => toast.error(t("ui.unexpectedError")));
      } finally {
        setProcessing(false);
      }
    },
    [mutate, t, tenantId, onSuccess],
  );

  return { onDelete, processing };
}
